import api from '../utils/api';

export const videoService = {
  /**
   * Get video metadata (duration, etc.) from provider
   * @param {'youtube' | 'vdocipher'} provider
   * @param {string} id - Video ID or URL
   * @returns {Promise<{duration: number, title: string, thumbnail: string}>}
   */
  getVideoMetadata: async (provider, id) => {
    // The api.get method returns response.data directly per the api.js implementation
    // Our backend returns { status: 'success', data: { duration, ... } }
    const response = await api.get('/api/videos/metadata', {
      params: { provider, id: id.trim() }
    });
    
    return response.data; // access the inner 'data' object from backend response
  },

  /**
   * Get upload credentials for VdoCipher
   * @param {string} title
   * @returns {Promise<any>}
   */
  getUploadCredentials: async (title) => {
    const response = await api.get('/api/vdocipher/upload-credentials', {
      params: { title }
    });
    return response.data;
  },

  /**
   * Upload video file directly to VdoCipher
   * @param {File} file
   * @param {object} uploadCredentials
   * @param {function} onProgress
   * @returns {Promise<string>} videoId
   */
  uploadToVdoCipher: async (file, uploadCredentials, onProgress) => {
    const clientPayload = uploadCredentials.clientPayload;
    if (!clientPayload) throw new Error("Invalid upload credentials");

    const formData = new FormData();
    // Order matters for S3
    if (clientPayload.key) formData.append('key', clientPayload.key);
    if (clientPayload['x-amz-credential']) formData.append('x-amz-credential', clientPayload['x-amz-credential']);
    if (clientPayload['x-amz-algorithm']) formData.append('x-amz-algorithm', clientPayload['x-amz-algorithm']);
    if (clientPayload['x-amz-date']) formData.append('x-amz-date', clientPayload['x-amz-date']);
    if (clientPayload.policy) formData.append('policy', clientPayload.policy);
    if (clientPayload['x-amz-signature']) formData.append('x-amz-signature', clientPayload['x-amz-signature']);
    
    formData.append('success_action_status', '201');
    formData.append('success_action_redirect', '');
     
    formData.append('file', file);
    
    // Use XHR or fetch. Here we standard fetch since we don't want base API interceptors
    // But axios is easier for progress. We can import axios just for this.
    // Since we are in a JS file in dashboard/src/services, let's assume axios is available or use XMLHttpRequest
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', clientPayload.uploadLink);
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 201) {
          resolve(uploadCredentials.videoId);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during upload"));
      
      xhr.send(formData);
    });
  }
};
